import './shotreview.css';
import type { GameRecord, ShotRecord } from '../net/protocol';
import { drawShotThumbnail } from './ShotThumbnail';
import { t } from './i18n';

/**
 * Right-side shot-review panel: lists every shot of a recorded game with a small
 * 2D layout+route thumbnail and per-row action icons (replay / practice), plus an
 * enlarged preview of the selected shot. Opened from the end-of-game banner
 * (in-room replay / try-this-layout) and from the personal history view (replay /
 * practice). The App supplies the action set + handlers so the same panel serves
 * both contexts. Practising a shot deliberately leaves the panel open so the
 * player can pick another shot.
 */
export interface ReviewAction {
  key: string;
  label: string;
  primary?: boolean;
}

export interface ShotReviewOptions {
  title: string;
  subtitle?: string;
  /** Per-shot actions, rendered as an icon button on every row. */
  actions: ReviewAction[];
  onAction(key: string, record: ShotRecord): void;
  onClose?(): void;
  /** Optional Back button (history view: return to the game list). */
  onBack?(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/** Glyph for an action key, shown on the per-row icon buttons. */
function actionIcon(key: string): string {
  if (key === 'replay') return '▶';
  if (key === 'practice') return '🎯';
  if (key === 'share') return '📣';
  return '•';
}

const DPR = () => Math.min(2, window.devicePixelRatio || 1);

export class ShotReviewPanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly titleEl = el('div', 'review-title');
  private readonly subEl = el('div', 'review-sub');
  private readonly preview = el('canvas', 'review-preview') as HTMLCanvasElement;
  private readonly caption = el('div', 'review-caption');
  private readonly actionsEl = el('div', 'review-actions');
  private readonly list = el('div', 'review-list');

  private game: GameRecord | null = null;
  private opts: ShotReviewOptions | null = null;
  private selected = 0;
  /** Deferred thumbnail-draw queue (drawn a few per frame so opening stays snappy). */
  private drawQueue: (() => void)[] = [];
  private drawScheduled = false;

  constructor(root: HTMLElement) {
    this.panel.id = 'review-panel';
    this.panel.style.display = 'none';

    const header = el('div', 'review-header');
    const close = el('button', 'review-close');
    close.textContent = t('mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.opts?.onClose?.();
    };
    const heads = el('div', 'review-heads');
    heads.append(this.titleEl, this.subEl);
    header.append(heads, close);

    this.preview.width = 320;
    this.preview.height = 180;

    this.panel.append(header, this.preview, this.caption, this.actionsEl, this.list);
    root.appendChild(this.panel);
  }

  get isOpen(): boolean {
    return this.panel.style.display !== 'none';
  }

  open(game: GameRecord, opts: ShotReviewOptions): void {
    this.game = game;
    this.opts = opts;
    this.selected = game.shots.length ? 0 : -1;
    this.titleEl.textContent = opts.title;
    this.subEl.textContent = opts.subtitle ?? '';
    this.subEl.style.display = opts.subtitle ? '' : 'none';
    this.renderList();
    this.renderSelection();
    this.panel.style.display = 'flex';
  }

  hide(): void {
    this.panel.style.display = 'none';
    this.drawQueue = [];
  }

  /** Fully dismiss (called when a new game starts): hide + forget the game. */
  dismiss(): void {
    this.hide();
    this.game = null;
    this.opts = null;
  }

  private select(i: number): void {
    this.selected = i;
    this.renderSelection();
    for (const row of Array.from(this.list.children)) {
      row.classList.toggle('selected', Number((row as HTMLElement).dataset.i) === i);
    }
  }

  private resultLabel(s: ShotRecord): string {
    if (s.result.foul) return t('review.foul');
    if (s.result.scored) return t('review.scored', { p: s.result.points });
    return t('review.miss');
  }

  private renderList(): void {
    const game = this.game;
    const opts = this.opts;
    if (!game || !opts) return;
    this.list.replaceChildren();
    this.drawQueue = [];
    game.shots.forEach((s, i) => {
      const row = el('div', 'review-row' + (i === this.selected ? ' selected' : ''));
      row.dataset.i = String(i);
      const thumb = el('canvas', 'review-thumb') as HTMLCanvasElement;
      const cw = 96;
      const ch = 54;
      thumb.width = cw * DPR();
      thumb.height = ch * DPR();
      thumb.style.width = `${cw}px`;
      thumb.style.height = `${ch}px`;
      // defer the (re-simulated) thumbnail draw so opening the panel is instant
      this.drawQueue.push(() => drawShotThumbnail(thumb, s, game.variant, game.physics));

      const meta = el('div', 'review-rowmeta');
      const line1 = el('div', 'review-rowtitle');
      line1.textContent = t('review.shotN', { n: i + 1 }) + ' · ' + s.shooterName;
      const line2 = el('div', 'review-rowsub' + (s.result.foul ? ' foul' : s.result.scored ? ' scored' : ''));
      line2.textContent = this.resultLabel(s);
      meta.append(line1, line2);

      // per-row action icons (replay / practice) — act on THIS shot directly
      const icons = el('div', 'review-rowicons');
      for (const a of opts.actions) {
        const btn = el('button', 'review-icon' + (a.primary ? ' primary' : ''));
        btn.textContent = actionIcon(a.key);
        btn.title = a.label;
        btn.onclick = (e) => {
          e.stopPropagation(); // don't also trigger the row-select
          this.select(i);
          opts.onAction(a.key, s);
        };
        icons.appendChild(btn);
      }

      row.append(thumb, meta, icons);
      row.onclick = () => this.select(i);
      this.list.appendChild(row);
    });
    this.scheduleDraws();
  }

  private scheduleDraws(): void {
    if (this.drawScheduled) return;
    this.drawScheduled = true;
    const step = () => {
      this.drawScheduled = false;
      const batch = this.drawQueue.splice(0, 4);
      for (const draw of batch) draw();
      if (this.drawQueue.length) this.scheduleDraws();
    };
    requestAnimationFrame(step);
  }

  private renderSelection(): void {
    const game = this.game;
    const opts = this.opts;
    if (!game || !opts) return;
    const s = game.shots[this.selected];
    this.actionsEl.replaceChildren();
    if (!s) {
      this.caption.textContent = t('review.empty');
      if (opts.onBack) this.actionsEl.appendChild(this.backButton(opts));
      return;
    }
    this.preview.width = 320 * DPR();
    this.preview.height = 180 * DPR();
    this.preview.style.width = '320px';
    this.preview.style.height = '180px';
    drawShotThumbnail(this.preview, s, game.variant, game.physics);

    const cue = s.cue;
    this.caption.textContent = t('review.caption', {
      n: this.selected + 1,
      name: s.shooterName,
      power: Math.round((cue.speed / 7) * 100), // rough % of a hard hit for display
      result: this.resultLabel(s),
    });

    // the per-shot actions live on each row now; the bar only holds Back (history)
    if (opts.onBack) this.actionsEl.appendChild(this.backButton(opts));
  }

  private backButton(opts: ShotReviewOptions): HTMLButtonElement {
    const back = el('button', 'review-action');
    back.textContent = t('review.back');
    back.onclick = () => opts.onBack?.();
    return back;
  }
}
